import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { client } from "@/lib/api";
import { SUBMIT } from "@/constants/testIds";
import { toast } from "sonner";
import { useAuth } from "@/components/AuthProvider";
import { IKO, IKO_KEYS, KILI, chipStyle } from "@/lib/design";

// Structured member-gated submission form.
// No @handle field — pulled from the account (Telegram provider) or optional profile field.
export default function SelfSubmit() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: "",
    iko: "",
    date: "",
    start_time: "",
    end_time: "",
    venue_name: "",
    location: "",
    is_free: false,
    price_amount: "",
    price_currency: "KES",
    description: "",
    ticket_link: "",
  });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // If not logged in (and not already routed to /join), gate the page.
    if (!loading && !user) {
      navigate(`/join?next=${encodeURIComponent("/submit")}`, { replace: true });
    }
    // Members without can_submit shouldn't happen (default entitlement), but guard anyway.
    if (!loading && user && !(user.entitlements || []).includes("can_submit")) {
      toast.error("Your account lacks can_submit — contact an admin.");
    }
  }, [loading, user, navigate]);

  const canSubmit = user && (user.entitlements || []).includes("can_submit") &&
    form.title.trim() && form.iko && form.date && form.start_time && form.venue_name.trim() && form.description.trim();

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return toast.error("Please fill title, iko, date, start time, venue and description.");
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("iko", form.iko);
      fd.append("date", form.date);
      fd.append("start_time", form.start_time);
      fd.append("end_time", form.end_time);
      fd.append("venue_name", form.venue_name);
      fd.append("location", form.location);
      fd.append("is_free", form.is_free ? "true" : "false");
      fd.append("price_amount", form.price_amount);
      fd.append("price_currency", form.price_currency);
      fd.append("description", form.description);
      fd.append("ticket_link", form.ticket_link);
      if (file) fd.append("file", file);
      await client.post("/member/submit/multipart", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSending(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="theme-night min-h-screen flex items-center justify-center" style={{ background: "var(--bg-night)", color: "var(--text-night)" }}>
        <p className="eyebrow">Loading…</p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="theme-night min-h-screen flex items-center justify-center" style={{ background: "var(--bg-night)", color: "var(--text-night)" }}>
        <div className="max-w-md rounded-2xl border p-10 text-center" style={{ borderColor: "var(--line-night)", background: "var(--card-night)" }}>
          <span className="compass-mark mx-auto" />
          <p className="eyebrow mt-4" style={{ color: "var(--text-muted-night)" }}>ASANTE</p>
          <h1 className="font-display mt-2 text-3xl">Received.</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--text-muted-night)" }}>A human reviews everything before it goes live on watuwako.com.</p>
          <div className="mt-6 flex gap-3 justify-center">
            <Link to="/app" className="rounded-full px-5 py-2 text-sm font-semibold" style={{ background: KILI, color: "#1A1A30" }}>Browse events</Link>
            <button onClick={() => { setDone(false); setForm({ ...form, title: "", description: "" }); }} className="rounded-full border px-5 py-2 text-sm font-semibold" style={{ borderColor: "var(--line-night)" }}>
              Submit another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="theme-night min-h-screen" style={{ background: "var(--bg-night)", color: "var(--text-night)" }}>
      <div className="mx-auto max-w-xl px-6 py-12">
        <Link to="/" className="flex items-center gap-3">
          <span className="compass-mark" /><span className="wordmark text-lg">watu wako</span>
        </Link>
        <p className="eyebrow mt-8" style={{ color: "var(--text-muted-night)" }}>SUBMIT AN EVENT</p>
        <h1 className="font-display mt-3 text-4xl">Tell us what's on.</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--text-muted-night)" }}>
          Posting as <strong>{user.display_name || user.name || user.email}</strong>{user.handle && <> · @{user.handle}</>}.
        </p>

        <form data-testid={SUBMIT.form} onSubmit={submit} className="mt-8 space-y-5">
          <Field label="Event title" required>
            <input data-testid={SUBMIT.titleInput} className="w-full rounded-md border bg-transparent p-3" style={{ borderColor: "var(--line-night)", color: "var(--text-night)" }}
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Sherehe under the stars" />
          </Field>

          <Field label="Iko (category)" required>
            <div className="flex flex-wrap gap-2">
              {IKO_KEYS.map((k) => {
                const active = form.iko === k;
                return (
                  <button
                    key={k}
                    type="button"
                    data-testid={SUBMIT.ikoSelector(k)}
                    onClick={() => setForm({ ...form, iko: k })}
                    className="rounded-full border px-4 py-2 text-sm font-semibold transition-transform hover:-translate-y-0.5"
                    style={active
                      ? { background: IKO[k].hex, borderColor: IKO[k].hex, color: "#111" }
                      : { ...chipStyle(k), borderColor: `${IKO[k].hex}77` }}
                  >{IKO[k].label}</button>
                );
              })}
            </div>
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Date" required>
              <input data-testid={SUBMIT.dateInput} type="date" className="w-full rounded-md border bg-transparent p-3 text-sm" style={{ borderColor: "var(--line-night)", color: "var(--text-night)", colorScheme: "dark" }}
                value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </Field>
            <Field label="Start" required>
              <input data-testid={SUBMIT.startTimeInput} type="time" className="w-full rounded-md border bg-transparent p-3 text-sm" style={{ borderColor: "var(--line-night)", color: "var(--text-night)", colorScheme: "dark" }}
                value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
            </Field>
            <Field label="End">
              <input data-testid={SUBMIT.endTimeInput} type="time" className="w-full rounded-md border bg-transparent p-3 text-sm" style={{ borderColor: "var(--line-night)", color: "var(--text-night)", colorScheme: "dark" }}
                value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
            </Field>
          </div>

          <Field label="Venue name" required>
            <input data-testid={SUBMIT.venueNameInput} className="w-full rounded-md border bg-transparent p-3" style={{ borderColor: "var(--line-night)", color: "var(--text-night)" }}
              value={form.venue_name} onChange={(e) => setForm({ ...form, venue_name: e.target.value })} placeholder="Alchemist, Kiza, Sarit…" />
          </Field>

          <Field label="Location / address" hint="Google Maps place picker coming later — for now paste an address or neighbourhood.">
            <input data-testid={SUBMIT.locationInput} className="w-full rounded-md border bg-transparent p-3" style={{ borderColor: "var(--line-night)", color: "var(--text-night)" }}
              value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Westlands, Nairobi" />
          </Field>

          <Field label="Price">
            <label className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted-night)" }}>
              <input data-testid={SUBMIT.freeToggle} type="checkbox" checked={form.is_free} onChange={(e) => setForm({ ...form, is_free: e.target.checked })} />
              Free entry
            </label>
            {!form.is_free && (
              <div className="mt-2 grid grid-cols-3 gap-2">
                <select data-testid={SUBMIT.priceCurrencyInput} value={form.price_currency} onChange={(e) => setForm({ ...form, price_currency: e.target.value })}
                  className="col-span-1 rounded-md border bg-transparent p-3 text-sm" style={{ borderColor: "var(--line-night)", color: "var(--text-night)" }}>
                  <option style={{ background: "#1A1A30" }} value="KES">KES</option>
                  <option style={{ background: "#1A1A30" }} value="USD">USD</option>
                  <option style={{ background: "#1A1A30" }} value="EUR">EUR</option>
                </select>
                <input data-testid={SUBMIT.priceAmountInput} className="col-span-2 rounded-md border bg-transparent p-3" style={{ borderColor: "var(--line-night)", color: "var(--text-night)" }}
                  placeholder='e.g. "1,500" or "1,000–2,500"'
                  value={form.price_amount} onChange={(e) => setForm({ ...form, price_amount: e.target.value })} />
              </div>
            )}
          </Field>

          <Field label="Description" required>
            <textarea data-testid={SUBMIT.descriptionInput} rows={6}
              className="w-full rounded-md border bg-transparent p-3" style={{ borderColor: "var(--line-night)", color: "var(--text-night)" }}
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What's happening? Who's playing? Anything a Nairobi visitor should know?" />
          </Field>

          <Field label="Ticket / booking link">
            <input data-testid={SUBMIT.ticketLinkInput} className="w-full rounded-md border bg-transparent p-3" style={{ borderColor: "var(--line-night)", color: "var(--text-night)" }}
              value={form.ticket_link} onChange={(e) => setForm({ ...form, ticket_link: e.target.value })} placeholder="https://…" />
          </Field>

          <Field label="Poster">
            <input data-testid={SUBMIT.imageInput} type="file" accept="image/*"
              className="w-full text-sm" style={{ color: "var(--text-muted-night)" }}
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </Field>

          <button
            data-testid={SUBMIT.submitButton}
            disabled={!canSubmit || sending}
            className="w-full rounded-full px-6 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40"
            style={{ background: KILI, color: "#1A1A30" }}
          >
            {sending ? "Sending…" : "Submit for review"}
          </button>
          <p className="text-xs" style={{ color: "var(--text-dim-night)" }}>Every submission passes through the human review queue before going live.</p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="eyebrow" style={{ color: "var(--text-muted-night)" }}>
        {label}{required && <span style={{ color: "var(--kili)", marginLeft: 4 }}>*</span>}
      </span>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-xs" style={{ color: "var(--text-dim-night)" }}>{hint}</p>}
    </label>
  );
}
